import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
	Card,
	Row,
	Col,
	Select,
	DatePicker,
	Table,
	Typography,
	Spin,
	Alert,
	Space,
	Tag,
	Button,
	Empty,
	Form,
	message,
	Modal,
} from 'antd';
import { Line } from '@ant-design/plots';
import { ClearOutlined, CopyOutlined, EditOutlined, RedoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import apiClient from '@/utils/apiClient';
import {
	IChurnFilters,
	ICancelReasonItem,
	IChurnSummaryData,
	ICancelReasonsResponse,
	IChurnSummaryResponse,
} from '@/interfaces/IPMetrics';
import TextArea from 'antd/es/input/TextArea';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DEFAULT_PAGE_LIMIT = 10;
const DEFAULT_DATE_RANGE: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs().subtract(30, 'days'), dayjs()];

interface ChurnDashboardProps {
	onViewDetail: (identifier: string) => void;
}

const ChurnDashboard: React.FC<ChurnDashboardProps> = ({ onViewDetail }) => {
	const [form] = Form.useForm();
	const [summaryData, setSummaryData] = useState<IChurnSummaryData | null>(null);
	const [churnReasons, setChurnReasons] = useState<ICancelReasonItem[]>([]);
	const [filters, setFilters] = useState<IChurnFilters>({ dateRange: DEFAULT_DATE_RANGE });

	const [categoryTrendData, setCategoryTrendData] = useState([]);
	const [sentimentTrendData, setSentimentTrendData] = useState([]);
	const [totalTrendData, setTotalTrendData] = useState([]);

	const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
	const [loadingReasons, setLoadingReasons] = useState<boolean>(true);
	const [loadingTrends, setLoadingTrends] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageStartKeys, setPageStartKeys] = useState<(Record<string, any> | null)[]>([null]);
	const [lastEvaluatedKey, setLastEvaluatedKey] = useState<Record<string, any> | null>(null);

	const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);
	const [editingRecord, setEditingRecord] = useState<ICancelReasonItem | null>(null);
	const [noteContent, setNoteContent] = useState('');
	const [isSavingNote, setIsSavingNote] = useState(false);

	const isInitialMount = useRef(true);

	const fetchTrendData = useCallback(async (currentFilters: IChurnFilters) => {
		if (!currentFilters.dateRange?.[0] || !currentFilters.dateRange?.[1]) {
			return;
		}
		setLoadingTrends(true);

		const [startDate, endDate] = currentFilters.dateRange;

		const params = {
			startDate: (startDate as dayjs.Dayjs).startOf('day').toISOString(),
			endDate: (endDate as dayjs.Dayjs).endOf('day').toISOString(),
		};

		try {
			const [categoryRes, sentimentRes, totalRes] = await Promise.all([
				apiClient.request({
					endPoint: '/api/admin/cancel-reasons/trends',
					method: 'GET',
					params: { ...params, groupBy: 'category' },
					clientType: 'ADMIN',
				}),
				apiClient.request({
					endPoint: '/api/admin/cancel-reasons/trends',
					method: 'GET',
					params: { ...params, groupBy: 'sentiment' },
					clientType: 'ADMIN',
				}),
				apiClient.request({
					endPoint: '/api/admin/cancel-reasons/trends',
					method: 'GET',
					params: { ...params, groupBy: 'total' },
					clientType: 'ADMIN',
				}),
			]);

			setCategoryTrendData(categoryRes.data);
			setSentimentTrendData(sentimentRes.data);
			setTotalTrendData(totalRes.data);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load trend data.');
		} finally {
			setLoadingTrends(false);
		}
	}, []);

	const fetchChurnReasonsData = useCallback(
		async (keyForPagination: Record<string, any> | null = null, currentFilters: IChurnFilters) => {
			setLoadingReasons(true);
			const params: any = { limit: DEFAULT_PAGE_LIMIT };
			if (currentFilters.category) {
				params.category = currentFilters.category;
			}
			if (currentFilters.sentiment) {
				params.sentiment = currentFilters.sentiment;
			}
			if (currentFilters.dateRange?.[0] && currentFilters.dateRange?.[1]) {
				const [startDate, endDate] = currentFilters.dateRange;
				params.startDate = (startDate as dayjs.Dayjs).startOf('day').toISOString();
				params.endDate = (endDate as dayjs.Dayjs).endOf('day').toISOString();
			}
			if (keyForPagination) {
				params.lastKey = JSON.stringify(keyForPagination);
			}

			try {
				const response: ICancelReasonsResponse = await apiClient.request({
					endPoint: '/api/admin/cancel-reasons/',
					method: 'GET',
					params,
					clientType: 'ADMIN',
				});
				setChurnReasons(response.data);
				setLastEvaluatedKey(response.lastEvaluatedKey);
				setError(null);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load churn reasons.');
				setChurnReasons([]);
			} finally {
				setLoadingReasons(false);
			}
		},
		[],
	);

	useEffect(() => {
		const fetchSummaryData = async () => {
			setLoadingSummary(true);
			try {
				const response: IChurnSummaryResponse = await apiClient.request({
					endPoint: '/api/admin/cancel-reasons/summary',
					method: 'GET',
					clientType: 'ADMIN',
				});
				setSummaryData(response.data);
			} catch (err) {
				console.error('Failed to fetch summary data:', err);
			} finally {
				setLoadingSummary(false);
			}
		};

		fetchSummaryData();
		fetchTrendData(filters);
		fetchChurnReasonsData(null, filters);
	}, []);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		fetchTrendData(filters);
		setCurrentPage(1);
		setPageStartKeys([null]);
		fetchChurnReasonsData(null, filters);
	}, [filters, fetchChurnReasonsData, fetchTrendData]);

	const handleFilterSubmit = (formValues: any) => {
		setFilters(formValues);
	};
	const handleResetFilters = () => {
		const newFilters = { dateRange: DEFAULT_DATE_RANGE };
		form.setFieldsValue(newFilters);
		setFilters(newFilters);
	};

	const handleNextPage = () => {
		if (lastEvaluatedKey) {
			setPageStartKeys((prev) => {
				const newKeys = [...prev];
				newKeys[currentPage] = lastEvaluatedKey;
				return newKeys;
			});
			fetchChurnReasonsData(lastEvaluatedKey, filters);
			setCurrentPage((prev) => prev + 1);
		}
	};

	const handlePrevPage = () => {
		if (currentPage > 1) {
			const keyForPrevPage = pageStartKeys[currentPage - 2];
			fetchChurnReasonsData(keyForPrevPage, filters);
			setCurrentPage((prev) => prev - 1);
		}
	};

	const handleOpenNoteModal = (record: ICancelReasonItem) => {
		setEditingRecord(record);
		setNoteContent(record.note_details?.note || '');
		setIsNoteModalVisible(true);
	};

	const handleCancelNote = () => {
		setIsNoteModalVisible(false);
		setEditingRecord(null);
		setNoteContent('');
	};

	const handleSaveNote = async () => {
		if (!editingRecord) {
			return;
		}

		setIsSavingNote(true);
		try {
			await apiClient.request({
				endPoint: '/api/admin/cancel-reasons/note',
				method: 'PUT',
				clientType: 'ADMIN',
				body: {
					number: editingRecord.number,
					date: editingRecord.date,
					notes: noteContent,
				},
			});

			setChurnReasons((prevReasons) =>
				prevReasons.map((reason) =>
					reason.number === editingRecord.number && reason.date === editingRecord.date
						? {
								...reason,
								note_details: {
									note: noteContent,
									updatedAt: new Date().toISOString(),
								},
							}
						: reason,
				),
			);

			message.success('Note saved successfully!');
			handleCancelNote();
		} catch (err) {
			message.error(err instanceof Error ? err.message : 'Failed to save note.');
		} finally {
			setIsSavingNote(false);
		}
	};

	const columns = [
		{
			title: 'Date',
			dataIndex: 'date',
			key: 'date',
			render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
			sorter: (a: ICancelReasonItem, b: ICancelReasonItem) =>
				dayjs(a.date).unix() - dayjs(b.date).unix(),
			defaultSortOrder: 'descend' as const,
			width: 180,
		},
		{ title: 'User ID', dataIndex: 'number', key: 'number', ellipsis: true, width: 150 },
		{
			title: 'Email',
			dataIndex: 'emailAddress',
			key: 'emailAddress',
			render: (text: string) => {
				if (!text || text === 'N/A') {
					return 'N/A';
				}

				const handleCopy = (e: React.MouseEvent) => {
					e.stopPropagation();
					navigator.clipboard.writeText(text);
					message.success('Email copied!');
				};

				return (
					<Space size={4}>
						<Text ellipsis={{ tooltip: text }}>{text}</Text>
						<Button
							type="text"
							icon={<CopyOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
							onClick={handleCopy}
						/>
					</Space>
				);
			},
			width: 250,
		},
		{
			title: 'CSM',
			dataIndex: 'csm',
			key: 'csm',
			render: (text: string) => <Text ellipsis={{ tooltip: text }}>{text || 'N/A'}</Text>,
			width: 200,
		},
		{
			title: 'Reason',
			dataIndex: 'cancel_reason',
			key: 'cancel_reason',
			render: (text: string) => <Text ellipsis={{ tooltip: text }}>{text || 'N/A'}</Text>,
			width: 200,
		},
		{
			title: 'LLM Category',
			dataIndex: ['llm_analysis', 'category'],
			key: 'llm_category',
			render: (category: string) => (category ? <Tag>{category}</Tag> : 'N/A'),
			width: 150,
		},
		{
			title: 'LLM Subcategory',
			dataIndex: ['llm_analysis', 'subcategory'],
			key: 'llm_subcategory',
			render: (text: string) => <Text ellipsis={{ tooltip: text }}>{text || 'N/A'}</Text>,
			width: 200,
		},
		{
			title: 'Sentiment',
			dataIndex: ['llm_analysis', 'sentiment'],
			key: 'sentiment',
			render: (sentiment: string) =>
				sentiment ? (
					<Tag
						color={sentiment === 'positive' ? 'green' : sentiment === 'negative' ? 'red' : 'orange'}
					>
						{sentiment}
					</Tag>
				) : (
					'N/A'
				),
			width: 120,
		},
		{
			title: 'Stripe Feedback',
			dataIndex: ['stripe_cancel_reasons', 'feedback'],
			key: 'stripe_feedback',
			render: (text: string) => text || 'N/A',
			width: 150,
		},
		{
			title: 'Notes',
			dataIndex: ['note_details', 'note'],
			key: 'notes',
			width: 150,
			render: (note, record: ICancelReasonItem) => (
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Text ellipsis={{ tooltip: note || 'No note' }}>{note || 'No note'}</Text>
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							handleOpenNoteModal(record);
						}}
						style={{ marginLeft: '8px' }}
					/>
				</div>
			),
		},
	];

	const commonLineChartConfig = {
		xField: 'time',
		yField: 'count',
		xAxis: {
			label: {
				autoHide: true,
				autoRotate: false,
			},
		},
		yAxis: {
			title: {
				text: 'Number of Churns',
			},
		},
		height: 300,
		autoFit: true,
		smooth: true,
		legend: {
			position: 'top-right' as const,
		},
	};

	return (
		<Space direction="vertical" size="large" style={{ width: '100%' }}>
			<Title level={2}>Churn Reason Analyzer</Title>

			<Card>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleFilterSubmit}
					initialValues={{ dateRange: DEFAULT_DATE_RANGE }}
				>
					<Row gutter={[16, 16]} align="bottom">
						<Col xs={24} sm={12} md={8} lg={7}>
							<Form.Item name="dateRange" label="Date Range (for all charts and table):">
								<RangePicker style={{ width: '100%' }} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={8} lg={6}>
							<Form.Item name="category" label="Filter Table by Category:">
								<Select
									style={{ width: '100%' }}
									placeholder="Filter by LLM Category"
									allowClear
									loading={loadingSummary}
								>
									{summaryData?.categories &&
										Object.keys(summaryData.categories).map((cat) => (
											<Option key={cat} value={cat}>
												{cat}
											</Option>
										))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={8} lg={6}>
							<Form.Item name="sentiment" label="Filter Table by Sentiment:">
								<Select
									style={{ width: '100%' }}
									placeholder="Filter by Sentiment"
									allowClear
									loading={loadingSummary}
								>
									{summaryData?.sentiments &&
										Object.keys(summaryData.sentiments)
											.filter((sent) => sent !== 'N/A')
											.map((sent) => (
												<Option key={sent} value={sent}>
													{sent}
												</Option>
											))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={5}>
							<Form.Item label=" " colon={false}>
								<Space>
									<Button icon={<ClearOutlined />} onClick={handleResetFilters}>
										Reset
									</Button>
									<Button type="primary" htmlType="submit" icon={<RedoOutlined />}>
										Apply Filters
									</Button>
								</Space>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Card>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => setError(null)}
				/>
			)}

			<Spin spinning={loadingTrends}>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Card title="Total Churn Trend">
							{totalTrendData.length > 0 ? (
								<Line
									{...commonLineChartConfig}
									data={totalTrendData}
									yField="value"
									legend={false}
								/>
							) : (
								<Empty description="No trend data for this period" />
							)}
						</Card>
					</Col>
				</Row>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Card title="Churn Trends by Category">
							{categoryTrendData.length > 0 ? (
								<Line
									{...commonLineChartConfig}
									data={categoryTrendData}
									seriesField="category"
									colorField="category"
								/>
							) : (
								<Empty description="No category trend data for this period" />
							)}
						</Card>
					</Col>
				</Row>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Card title="Churn Trends by Sentiment">
							{sentimentTrendData.length > 0 ? (
								<Line
									{...commonLineChartConfig}
									data={sentimentTrendData}
									seriesField="sentiment"
									colorField="sentiment"
								/>
							) : (
								<Empty description="No sentiment trend data for this period" />
							)}
						</Card>
					</Col>
				</Row>
			</Spin>

			<Card title="Detailed Churn Reasons">
				<Spin spinning={loadingReasons}>
					<Table
						dataSource={churnReasons}
						columns={columns}
						rowKey={(record) => `${record.number}-${record.date}`}
						pagination={false}
						scroll={{ x: 1200 }}
						onRow={(record) => {
							return {
								onClick: () => {
									onViewDetail(record.number);
								},
								style: { cursor: 'pointer' },
							};
						}}
					/>
					<Space style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
						<Text>Page: {currentPage}</Text>
						<Button onClick={handlePrevPage} disabled={currentPage === 1 || loadingReasons}>
							Previous
						</Button>
						<Button onClick={handleNextPage} disabled={!lastEvaluatedKey || loadingReasons}>
							Next
						</Button>
					</Space>
					{churnReasons.length === 0 && !loadingReasons && (
						<Empty description="No churn reasons match your filters." />
					)}
				</Spin>
			</Card>

			<Modal
				title="Edit Note"
				open={isNoteModalVisible}
				onOk={handleSaveNote}
				onCancel={handleCancelNote}
				confirmLoading={isSavingNote}
			>
				<TextArea rows={6} value={noteContent} onChange={(e) => setNoteContent(e.target.value)} />
			</Modal>
		</Space>
	);
};

export default ChurnDashboard;
